import { useEffect, useState } from 'react';
import { Copy, Plus, ShieldOff, Trash2, Pencil, Check, X } from 'lucide-react';
import { fetchApiKeys, createApiKey, revokeApiKey, deleteApiKey, updateApiKey, ApiKey } from '../lib/api';

function maskKey(key: string) {
  if (key.length <= 10) return key;
  return `${key.slice(0, 8)}${'•'.repeat(12)}${key.slice(-4)}`;
}

// Tier → creation limits, kept in sync with the Worker's API_TIER_LIMITS
// (backend/worker/src/index.ts) so the admin sees what each key actually gets.
const TIERS: Record<string, { label: string; detail: string; badge: string }> = {
  free:  { label: 'Free',  detail: '10/min · 50/day · CAPTCHA after every 3 inboxes', badge: 'bg-gray-100 text-gray-700 dark:bg-gray-700/40 dark:text-gray-300' },
  basic: { label: 'Basic', detail: '30/min · 500/day', badge: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  pro:   { label: 'Pro',   detail: '100/min · unlimited daily', badge: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
};

function ApiKeys() {
  const [keys, setKeys] = useState<ApiKey[]>([]);
  const [label, setLabel] = useState('');
  const [creating, setCreating] = useState(false);
  const [revealedKey, setRevealedKey] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);
  const [editLabel, setEditLabel] = useState('');
  const [editRpm, setEditRpm] = useState(60);
  const [editTier, setEditTier] = useState('free');
  const [saving, setSaving] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const load = async () => {
    try { setKeys(await fetchApiKeys()); } catch { /* ignore */ }
  };

  useEffect(() => { load(); }, []);

  const create = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!label.trim()) return;
    setCreating(true);
    setError(null);
    try {
      const result = await createApiKey(label.trim());
      if (!result || !result.key) { setError('Failed to create key'); setCreating(false); return; }
      setRevealedKey(result.key);
      setLabel('');
      load();
    } catch (err: any) {
      setError(err?.message || 'Failed to create API key');
    }
    setCreating(false);
  };

  const revoke = async (id: string) => {
    if (!confirm('Revoke this key? Any developer using it will immediately lose access.')) return;
    try { await revokeApiKey(id); load(); } catch { setError('Failed to revoke key'); }
  };

  const remove = async (id: string) => {
    if (!confirm('Permanently delete this key record?')) return;
    try { await deleteApiKey(id); load(); } catch { setError('Failed to delete key'); }
  };

  const startEdit = (k: ApiKey) => {
    setEditing(k.id);
    setEditLabel(k.label);
    setEditRpm(k.rate_limit_rpm || 60);
    setEditTier(k.tier || 'free');
  };

  const saveEdit = async (id: string) => {
    setSaving(true);
    await updateApiKey(id, { label: editLabel, rate_limit_rpm: editRpm, tier: editTier });
    setSaving(false);
    setEditing(null);
    load();
  };

  return (
    <div>
      <h1 className="text-3xl font-bold mb-8 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
        Developer API Keys
      </h1>

      {revealedKey && (
        <div className="mb-6 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-xl p-4 flex items-center justify-between gap-4">
          <div>
            <p className="text-sm font-medium text-green-800 dark:text-green-300">New key created — copy it now, it won't be shown again in full:</p>
            <code className="text-sm break-all text-green-900 dark:text-green-200">{revealedKey}</code>
          </div>
          <button
            onClick={() => { navigator.clipboard.writeText(revealedKey); setRevealedKey(null); }}
            className="shrink-0 inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            <Copy size={14} /> Copy
          </button>
        </div>
      )}

      {error && (
        <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      <form onSubmit={create} className="mb-8 flex gap-3 flex-wrap">
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Partner XYZ)"
          className="px-4 py-2 rounded-xl border border-gray-300 bg-white dark:bg-gray-800 dark:border-gray-600 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-1 min-w-[200px]"
        />
        <button
          type="submit" disabled={creating}
          className="inline-flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl hover:from-blue-600 hover:to-blue-700 shadow-lg transition-all disabled:opacity-50"
        >
          <Plus size={16} /> Issue new key
        </button>
      </form>

      <div className="bg-white/80 backdrop-blur-md rounded-2xl shadow-xl border border-gray-200/60 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50/80 dark:bg-gray-700/30">
              <tr>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Label</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Key</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Requests</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Tier</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Rate limit</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Last used</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Status</th>
                <th className="text-left p-4 font-semibold text-gray-700 dark:text-gray-300">Actions</th>
              </tr>
            </thead>
            <tbody>
              {keys.map((k) => (
                <tr key={k.id} className="border-t border-gray-200/60 dark:border-gray-700/60 hover:bg-gray-50/50 dark:hover:bg-gray-700/30 transition-colors">
                  <td className="p-4 font-medium text-gray-900 dark:text-gray-100">
                    {editing === k.id ? (
                      <input
                        value={editLabel}
                        onChange={(e) => setEditLabel(e.target.value)}
                        className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 w-36"
                      />
                    ) : k.label}
                  </td>
                  <td className="p-4 text-gray-600 dark:text-gray-400 font-mono text-xs">{maskKey(k.key)}</td>
                  <td className="p-4 text-gray-600 dark:text-gray-400">{k.request_count}</td>
                  <td className="p-4">
                    {editing === k.id ? (
                      <select
                        value={editTier}
                        onChange={(e) => setEditTier(e.target.value)}
                        title={TIERS[editTier]?.detail}
                        className="px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                      >
                        <option value="free">Free</option>
                        <option value="basic">Basic</option>
                        <option value="pro">Pro</option>
                      </select>
                    ) : (
                      <span
                        title={TIERS[k.tier || 'free']?.detail}
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${(TIERS[k.tier || 'free'] || TIERS.free).badge}`}
                      >
                        {(TIERS[k.tier || 'free'] || TIERS.free).label}
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-gray-600 dark:text-gray-400">
                    {editing === k.id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="number" min={1} max={1000}
                          value={editRpm}
                          onChange={(e) => setEditRpm(Number(e.target.value))}
                          className="w-20 px-2 py-1 rounded-lg border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 text-sm focus:outline-none focus:ring-1 focus:ring-blue-500"
                        />
                        <span className="text-xs text-gray-400">/min</span>
                      </div>
                    ) : `${k.rate_limit_rpm ?? 60} req/min`}
                  </td>
                  <td className="p-4 text-gray-600 dark:text-gray-400">
                    {k.last_used_at ? new Date(k.last_used_at * 1000).toLocaleString() : 'Never'}
                  </td>
                  <td className="p-4">
                    <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                      k.revoked_at
                        ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
                        : 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    }`}>
                      {k.revoked_at ? 'Revoked' : 'Active'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2 items-center">
                      {editing === k.id ? (
                        <>
                          <button onClick={() => saveEdit(k.id)} disabled={saving} title="Save" className="text-green-600 hover:text-green-800 dark:text-green-400 disabled:opacity-50">
                            <Check size={16} />
                          </button>
                          <button onClick={() => setEditing(null)} title="Cancel" className="text-gray-500 hover:text-gray-700 dark:text-gray-400">
                            <X size={16} />
                          </button>
                        </>
                      ) : (
                        <button onClick={() => startEdit(k)} title="Edit" className="text-blue-600 hover:text-blue-800 dark:text-blue-400">
                          <Pencil size={15} />
                        </button>
                      )}
                      {!k.revoked_at && (
                        <button onClick={() => revoke(k.id)} title="Revoke" className="text-orange-600 hover:text-orange-800 dark:text-orange-400">
                          <ShieldOff size={16} />
                        </button>
                      )}
                      <button onClick={() => remove(k.id)} title="Delete" className="text-red-600 hover:text-red-800 dark:text-red-400">
                        <Trash2 size={16} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {keys.length === 0 && <p className="p-8 text-center text-gray-500 dark:text-gray-400">No API keys yet. Issue one above.</p>}
      </div>
    </div>
  );
}

export default ApiKeys;
